import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Stepper, Step, StepLabel, StepConnector, Typography, Box, Tooltip } from "@mui/material";
import Check from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";

const SuccessionStepper = ({ steps }) => {
  const today = new Date();
  const { t } = useTranslation();

  const sortedSteps = useMemo(() => {
    return [...(steps || [])].sort((a, b) => new Date(a.planDate) - new Date(b.planDate));
  }, [steps]);

  // 2. Efficient Progress Calculation: Only re-calculate if sortedSteps changes
  const currentActive = useMemo(() => {
    const today = new Date().setHours(0, 0, 0, 0); // Normalize to start of day
    const activeStepIndex = sortedSteps.findIndex(
      (s) => new Date(s.planDate).setHours(0, 0, 0, 0) >= today
    );
    return activeStepIndex === -1 ? sortedSteps.length : activeStepIndex;
  }, [sortedSteps]);

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        display: "block",
        WebkitOverflowScrolling: "touch", // Smooth scroll for mobile
        pb: 1,
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#bdbdbd",
          borderRadius: 10,
        },
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Stepper
          alternativeLabel
          sx={{
            width: "max-content", // This forces the Stepper to be wide
            minWidth: "100%",
            display: "flex",
            flexWrap: "nowrap", // Prevents stacking
            flexDirection: "row",
            pt: 2,
            pb: 2,
          }}
          activeStep={currentActive}
          connector={
            <StepConnector
              sx={{
                // THE LINE LOGIC
                [`& .MuiStepConnector-line`]: {
                  borderTopWidth: 4,
                  borderRadius: 1,
                  borderColor: "#eaeaf0 ", // Default Grey for future
                },
                // This makes the line GREEN if the step is finished OR currently active
                [`&.Mui-completed .MuiStepConnector-line, &.Mui-active .MuiStepConnector-line`]: {
                  borderColor: "#4caf50 ", // Progress Green
                },
              }}
            />
          }
        >
          {sortedSteps.map((step, index) => {
            const isCompleted = index < currentActive;
            const isActive = index === currentActive;
            const isLongJobName = (step.jobName || "").length > 15;

            return (
              <Step
                key={step.id}
                sx={{
                  flex: "0 0 auto", // CRITICAL FIX
                  minWidth: 140,
                }}
              >
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        bgcolor: isCompleted ? "#4caf50" : isActive ? "#fb8c00" : "#9e9e9e",
                        color: "#fff",
                      }}
                    >
                      {isCompleted ? <Check sx={{ fontSize: 20 }} /> : index + 1}
                    </Box>
                  )}
                  sx={{
                    "& .MuiStepLabel-label": {
                      mt: 1,
                      fontSize: "0.75rem",
                      // Text color logic
                      color: isActive ? "#1976d2 " : "#7b809a ",
                      fontWeight: isActive ? "bold" : "normal",
                    },
                  }}
                >
                  {/* Step Header */}
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      fontWeight: "bold",
                      color: "#344767",
                      textTransform: "none",
                      mb: 1,
                    }}
                  >
                    {t("admin.successionPlanSteps.step")} {index + 1}
                  </Typography>

                  {/* Job Name */}
                  <Tooltip
                    title={isLongJobName ? step.jobName : ""}
                    arrow
                    disableHoverListener={!isLongJobName}
                  >
                    <Typography
                      variant="caption"
                      display="block"
                      noWrap
                      sx={{
                        lineHeight: 1.1,
                        color: "#ECEFF1",
                        maxWidth: 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {step.jobName}
                    </Typography>
                  </Tooltip>

                  {/* Date */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.6rem",
                      opacity: 0.8,
                      color: "#ECEFF1",
                    }}
                  >
                    {step.planDate}
                  </Typography>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </Box>
  );
};

SuccessionStepper.propTypes = {
  steps: PropTypes.array.isRequired,
};

export default SuccessionStepper;
